import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { ProductVariant } from '@/api/models';
import { asSpan } from './productDetailTypography';

type ProductVariantBarcodesProps = {
    variant: ProductVariant;
    /** Opens the barcodes drawer for this variant. */
    onEdit: (variantId: string) => void;
};

/**
 * An open variant's barcodes (bos-app#224, design board **ProductDetail**,
 * Barcodes card): the main barcode with its Main chip, then each alternate,
 * or a line saying there are none, and **Edit barcodes**, which opens the
 * barcodes drawer where they are added, removed and made main.
 */
export const ProductVariantBarcodes: React.FC<ProductVariantBarcodesProps> = ({
    variant,
    onEdit,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Card
            className="w-full gap-3"
            variant="secondary"
            {...getNodeTestIdProp(`product-variant-barcodes-${variant.id}`)}
        >
            <Card.Header className="flex flex-row flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <Card.Title>
                        {t('products.detail.barcodes.title')}
                    </Card.Title>
                    <Card.Description>
                        {t('products.detail.barcodes.intro')}
                    </Card.Description>
                </div>
                <Button
                    onPress={() => onEdit(variant.id)}
                    size="sm"
                    variant="secondary"
                    {...getNodeTestIdProp(
                        `product-variant-barcodes-edit-${variant.id}`,
                    )}
                >
                    {t('products.detail.barcodes.edit')}
                </Button>
            </Card.Header>
            <Card.Content>
                <ul
                    aria-label={t('products.detail.barcodes.title')}
                    className="flex flex-col gap-2"
                >
                    <li
                        className="flex min-h-8 flex-wrap items-center gap-2"
                        {...getNodeTestIdProp(
                            `product-variant-barcode-main-${variant.id}`,
                        )}
                    >
                        <Typography
                            className="min-w-0 flex-1 break-all tabular-nums"
                            render={asSpan}
                            type="body-sm"
                            weight="medium"
                        >
                            {variant.barcode}
                        </Typography>
                        <Chip color="success" size="sm" variant="soft">
                            <Chip.Label>
                                {t('products.detail.barcodes.main')}
                            </Chip.Label>
                        </Chip>
                    </li>
                    {variant.alternateBarcodes.map((code) => (
                        <li
                            className="flex min-h-8 flex-wrap items-center gap-2"
                            key={code}
                            {...getNodeTestIdProp(
                                `product-variant-barcode-alternate-${variant.id}-${code}`,
                            )}
                        >
                            <Typography
                                className="min-w-0 flex-1 break-all tabular-nums"
                                render={asSpan}
                                type="body-sm"
                            >
                                {code}
                            </Typography>
                            <Typography
                                color="muted"
                                render={asSpan}
                                type="body-xs"
                            >
                                {t('products.detail.barcodes.alternate')}
                            </Typography>
                        </li>
                    ))}
                </ul>
                {variant.alternateBarcodes.length === 0 ? (
                    <Typography
                        className="mt-2"
                        color="muted"
                        type="body-sm"
                        {...getNodeTestIdProp(
                            `product-variant-barcodes-none-${variant.id}`,
                        )}
                    >
                        {t('products.detail.barcodes.none')}
                    </Typography>
                ) : null}
            </Card.Content>
        </Card>
    );
};
